import { getRequestLocale } from '../i18n/locales';

/**
 * Erro no formato RFC 7807, como o contrato define para toda operacao.
 *
 * Nenhuma tela le `response.json()` direto: o `orval` gera as chamadas contra
 * este mutator, e o mutator e o unico lugar que conhece o formato do erro.
 */
export interface Problem {
  readonly type: string;
  readonly title: string;
  readonly status: number;
  readonly detail?: string | null;
}

export class ProblemError extends Error {
  readonly type: string;
  readonly title: string;
  readonly status: number;
  readonly detail: string | null;

  constructor(problem: Problem) {
    super(`${problem.status} ${problem.title}`);
    this.name = 'ProblemError';
    this.type = problem.type;
    this.title = problem.title;
    this.status = problem.status;
    this.detail = problem.detail ?? null;
  }
}

export function isProblemError(error: unknown): error is ProblemError {
  return error instanceof ProblemError;
}

const PROBLEM_MEDIA_TYPE = 'application/problem+json';

function isProblem(value: unknown): value is Problem {
  if (value === null || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['type'] === 'string' &&
    typeof candidate['title'] === 'string' &&
    typeof candidate['status'] === 'number'
  );
}

async function toProblem(response: Response): Promise<Problem> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes(PROBLEM_MEDIA_TYPE) || contentType.includes('json')) {
    try {
      const body: unknown = await response.json();
      if (isProblem(body)) return body;
    } catch {
      // Corpo ilegivel cai no problema sintetico abaixo.
    }
  }
  return {
    type: 'about:blank',
    title: response.statusText || 'erro inesperado',
    status: response.status,
    detail: null,
  };
}

/**
 * Mutator do `orval`. Toda chamada HTTP contra a plataforma passa por aqui.
 *
 * `credentials: 'include'` porque a sessao e um cookie `HttpOnly`: nenhum token
 * e lido, escrito ou guardado em JavaScript, e o navegador cuida do envio.
 */
export async function apiFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set('Accept-Language', getRequestLocale());
  if (options.body !== undefined && options.body !== null && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    throw new ProblemError(await toProblem(response));
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export default apiFetch;
